import { useEffect, useState } from 'react';

import { getResourceKey, Logger } from '@futrli/shared-services';
import { Organisation } from '@futrli-sdk/organisations';
import { useErrorHandler } from '@futrli/errors';
import { useFinancialConfig } from '@futrli/views/hooks/useFinancialConfig';

import { fetchOrganisationById } from '../../core/api/workspace-coordinator';

interface OrganisationResult {
  failed: boolean;
  loading: boolean;
  organisation: Organisation | null;
  resourceKey: string;
}

export const usePredictManagementData = (
  workspaceId: string,
  organisationId: string,
  isOnboardingRoute: boolean
) => {
  const handleError = useErrorHandler();
  const resourceKey = getResourceKey(workspaceId, organisationId);
  const [organisationResult, setOrganisationResult] = useState<OrganisationResult>();

  // A warm config cache must not be paired with organisation details from the previous route.
  const currentResult = organisationResult?.resourceKey === resourceKey
    ? organisationResult
    : { organisation: null, failed: false, loading: true };
  const { organisation, failed, loading } = currentResult;
  const {
    financialConfig,
    error: financialConfigError,
    isLoading: isLoadingFinancialConfig
  } = useFinancialConfig({ workspaceId, entityType: 'organisation', entityId: organisationId });

  useEffect(() => {
    if (financialConfigError) {
      Logger.error(String(financialConfigError));
    }
  }, [financialConfigError]);

  // Onboarding can change organisation setup without changing its ID.
  useEffect(() => {
    let active = true;
    const loadData = async () => {
      setOrganisationResult({ resourceKey, organisation: null, failed: false, loading: true });
      try {
        const fetchedOrganisation = await fetchOrganisationById(workspaceId, organisationId);
        if (active) {
          setOrganisationResult({
            resourceKey,
            organisation: fetchedOrganisation,
            failed: false,
            loading: false
          });
        }
      } catch (error) {
        Logger.error(String(error));
        if (active) {
          setOrganisationResult({ resourceKey, organisation: null, failed: true, loading: false });
        }
      }
    };
    loadData();
    return () => {
      // The request may still finish after navigation or unmount.
      active = false;
    };
  }, [organisationId, workspaceId, isOnboardingRoute, resourceKey]);

  const refetchOrganisation = async () => {
    try {
      // Keep the last data so a failed refresh can restore the existing screen.
      setOrganisationResult(previous =>
        previous?.resourceKey === resourceKey ? { ...previous, loading: true } : previous
      );
      const fetchedOrganisation = await fetchOrganisationById(workspaceId, organisationId);
      setOrganisationResult(previous =>
        previous?.resourceKey === resourceKey
          ? { ...previous, organisation: fetchedOrganisation, loading: false }
          : previous
      );
    } catch (error) {
      setOrganisationResult(previous =>
        previous?.resourceKey === resourceKey ? { ...previous, loading: false } : previous
      );
      handleError(error);
    }
  };

  return {
    financialConfig,
    // Background config refreshes must not restore the full-page loader.
    isLoading: loading || isLoadingFinancialConfig,
    organisation,
    refetchOrganisation,
    shouldRedirect: failed && !!financialConfigError
  };
};
